import { useEffect, useState } from 'react';
import { CASHIER_DEVICES_CHANGED_EVENT } from '../constants';
import { fetchCashierDevicesWaitingCount } from '../services/api';

export const WAITING_DEVICE_POLL_MS = 30_000;

/**
 * Number of cashier devices waiting for the manager's approval (sidebar badge).
 * Refreshes on mount, every 30 s while the tab is visible, on window focus and on
 * `cashier-devices-changed`. Errors are ignored: the badge keeps its last value.
 */
export function useWaitingDeviceCount(enabled = true): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        const res = await fetchCashierDevicesWaitingCount();
        const next = res?.success ? Number(res.data?.count) : NaN;
        if (!cancelled && Number.isFinite(next)) setCount(Math.max(0, Math.floor(next)));
      } catch {
        /* badge only: keep the last value */
      }
    };

    const onEvent = () => {
      void refresh();
    };
    const onTick = () => {
      if (document.visibilityState === 'hidden') return;
      void refresh();
    };

    void refresh();
    const timer = window.setInterval(onTick, WAITING_DEVICE_POLL_MS);
    window.addEventListener('focus', onEvent);
    window.addEventListener(CASHIER_DEVICES_CHANGED_EVENT, onEvent);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onEvent);
      window.removeEventListener(CASHIER_DEVICES_CHANGED_EVENT, onEvent);
    };
  }, [enabled]);

  return count;
}
